/* eslint-disable max-lines */
// deps
import React, { ReactElement, useContext, memo, useMemo, useRef, useCallback, useState } from "react";
import invariant from "tiny-invariant";

// internal deps
import { PaymentProgressLoader, AddNewCard, CardPaymentMethodData } from "@swiggy-private/payment-ui";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import { PaymentActionProvider } from "../payment-action-provider";
import { GenericCrouton, GenericCroutonProps } from "@swiggy-private/core-ui";

// constants
import { options as cardGroupOptions } from "@payment-client/constants/Options";
import { MINIMUM_CARDNUMBER_LENGTH, SKIP_CLIENT_CARD_VALIDATION } from "@payment-client/constants/Card";
import { HEADER_TITLE } from "@payment-client/constants/Header";
import { CARD_BIN_NUMBER_MAX_LIMIT, defaultCardBrandDetails } from "@payment-client/constants/Card";
import { SCREEN_NAME } from "@payment-client/analytics/constants";
import { CARD_API_KEYS } from "@swiggy-private/checkout-core";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";
import { ICardOffer } from "@swiggy-private/payment-ui";
import { JuspayCardFingerprint } from "@payment-client/interfaces/Juspay";
import { CardBrandDetails, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";

// contexts
import { PlaceOrderContext } from "@payment-client/contexts/PlaceOrderContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { ApiContext, getBridgeSdk, recordNREvent, NEWRELIC_TABLES, ConfigContext } from "@swiggy-private/checkout-core";
import { JuspayContext } from "@payment-client/contexts/JuspayContext";
import { EmiSelectionContext } from "@payment-client/contexts/EmiSelectionContext";

// helpers
import { getPaymentHeaderSubtitleForSubRoutesV4 } from "@payment-client/helpers/PaymentHelper";
import { getItemCountAndAnnotation } from "@payment-client/helpers/CheckoutMetaHelper";
import { getCardDetails } from "@payment-client/apis/Card";
import {
    GlobalHeaderState,
    SwgyHeaderKeysList,
    PublicApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
} from "@swiggy-private/checkout-core";
import { getValidClientAuthToken, isCardRateLimitEnabled } from "@payment-client/helpers/CardHelper";
import { resolveMerchantId } from "@payment-client/helpers/JuspayHelpers";
import { Storage, captureError, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { checkOfferAndRewardOnCard } from "@payment-client/helpers/RewardsOffersHelper";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import {
    getEmiNotAvailableOnBankCroutonState,
    CROUTON_CLEAR_STATE,
    ADD_NEW_CARD_TEXTS,
} from "@swiggy-private/payment-ui";
import { isCategoryInstamart } from "@payment-client/helpers/Misc";

// hooks
import useCardEvents from "@payment-client/hooks/UseCardEvents";

// apis
import { fetchCardFingerprint } from "@payment-client/apis/Juspay";
import { getCardDetailsPublic } from "@payment-client/apis/publicCard";

// styles
import styles from "./styles.scss";

export type AddNewCardPaymentProps = RouteProps<{ paymentMethod: CardPaymentMethodData }>;

export const AddNewCardPayment = memo(function AddNewCardPayment({
    data,
}: AddNewCardPaymentProps): ReactElement | null {
    const { placeOrderInProgress, processingOrder } = useContext(PlaceOrderContext);
    const { checkoutMetaInfo, paymentResponse } = useContext(ShowPaymentContext);
    const { selectedEmiDetails } = useContext(EmiSelectionContext);
    const {
        tenure,
        bankCode,
        emiBank,
        emiType,
        txnEmiType,
        bankName = ADD_NEW_CARD_TEXTS.SELECTED,
    } = selectedEmiDetails || {};
    const { clientAuthTokenData, fetchJuspayClientAuthToken } = useContext(JuspayContext);
    const { paymentPageOfferEnabled, rewardsEnabled, selectedSplitPayMethods } = useContext(PaymentPayContext);
    const configContext = useContext(ConfigContext);
    const [skipClientCardValidation, setSkipClientCardValidation] = useState(
        !!Storage.getItem(SKIP_CLIENT_CARD_VALIDATION),
    );
    const [croutonState, setCroutonState] = useState<GenericCroutonProps>(CROUTON_CLEAR_STATE);
    const [isCardBankSameAsSelectedEmiBank, setIsCardBankSameAsSelectedEmiBank] = useState<boolean | null>(null);

    const { cartItemsCount, addressAnnotation } = useMemo(
        () => getItemCountAndAnnotation(checkoutMetaInfo),
        [checkoutMetaInfo],
    );

    const isCardDiscountStripEnabled = isCategoryInstamart();

    const saveCardCtaHelperText = useMemo(
        () => (isCategoryInstamart() ? ADD_NEW_CARD_TEXTS.SAVE_CARD_ON_IM_PAYMENT_MODE : undefined),
        [],
    );

    const {
        onSecureCardInfoClickAction,
        onSecureCardInfoView,
        onKnowMoreTokenizationClickAction,
        onProceedToPayClick,
        onSaveCardClick,
        onProceedToPayClickAction,
        onCardNotEligibleCroutonImpression,
    } = useCardEvents(SCREEN_NAME.ADD_NEW_CARD);

    const { amountToPay, withSplitPay } = useContext(PaymentPayContext);
    const apiContextValue = useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const apiCallRef = useRef(false);
    const apiHost = apiContextValue.host.unwrap();

    const [cardBrandDetails, setCardBrandDetails] = useState<CardBrandDetails>(defaultCardBrandDetails);
    const [cardOffer, setCardOffer] = useState<ICardOffer>();

    const [cardFingerPrintCallInProgress, setCardFingerPrintCallInProgress] = useState<boolean>(false);
    const [cardFingerPrint, setCardFingerPrint] = useState<JuspayCardFingerprint | null>(null);

    const isGetCardDetailsPublicApiEnable = useMemo(
        () =>
            isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CARDS_JUSPAY_APIS,
                CARD_API_KEYS.GET_CARD_DETAILS,
            ),
        [publicApiConfig],
    );

    const isGetCardOfferPublicApiEnable = useMemo(
        () =>
            isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CARDS_JUSPAY_APIS,
                CARD_API_KEYS.GET_CARD_OFFER,
            ),
        [publicApiConfig],
    );

    const getCardDetailsFn = isGetCardDetailsPublicApiEnable ? getCardDetailsPublic : getCardDetails;

    invariant(data?.paymentMethod, "Missing route data");

    const subtitleText = useMemo(() => {
        return getPaymentHeaderSubtitleForSubRoutesV4({
            selectedAddressAnnotation: addressAnnotation,
            cartAmount: String(amountToPay),
            itemCount: cartItemsCount,
            presentationDetails: paymentResponse?.presentationDetails,
        });
    }, [addressAnnotation, amountToPay, cartItemsCount, paymentResponse]);

    const checkIfCardBankIsSameAsSelectedEmiBank = useCallback(
        (isCardEligibleForSelectedEmi?: boolean) => {
            setIsCardBankSameAsSelectedEmiBank(isCardEligibleForSelectedEmi ?? null);

            if (isCardEligibleForSelectedEmi === false) {
                const croutonState = getEmiNotAvailableOnBankCroutonState(bankName);
                setCroutonState(croutonState);
                onCardNotEligibleCroutonImpression(croutonState.title, true);
            } else {
                setCroutonState({ ...CROUTON_CLEAR_STATE });
            }
        },
        [bankName, onCardNotEligibleCroutonImpression],
    );

    const getCardDetailsByBin = useCallback(
        (cardBin: string) => {
            if (apiCallRef.current) {
                return;
            }
            if (cardBin.length < CARD_BIN_NUMBER_MAX_LIMIT) {
                setCardBrandDetails(defaultCardBrandDetails);
                setCardOffer({
                    couponApplicable: true,
                    payableAmount: "",
                });
                setCroutonState({ ...CROUTON_CLEAR_STATE });
                setIsCardBankSameAsSelectedEmiBank(null);
                return;
            }

            apiCallRef.current = true;

            getCardDetailsFn(apiHost, cardBin, bankCode)
                .then((data) => {
                    apiCallRef.current = false;
                    if (data == null) {
                        return;
                    }

                    const cardBinNumber = data.bin;

                    const result: CardBrandDetails = {
                        cardBrand: data.cardBrand,
                        cardIcon: { id: data.imageId || "", padding: 1 },
                        url: data.fullImageUrl,
                        tokenizeSupport: data.tokenizeSupport,
                        cardBin: cardBinNumber,
                        disableForTenant: data.nonFoodDisabled || false,
                        disableForTenantReason: data.nonFoodDisabledReason || null,
                        country: data?.country || "",
                        enabled: data.enabled ?? true,
                        disableReason: data.disableReason || null,
                        isCardEligibleForSelectedEmi: data.isCardEligibleForSelectedEmi || false,
                    };
                    setCardBrandDetails(result);

                    checkIfCardBankIsSameAsSelectedEmiBank(data.isCardEligibleForSelectedEmi);

                    const shouldSkipClientCardValidation = data.skipClientCardValidation === true;
                    setSkipClientCardValidation(shouldSkipClientCardValidation);
                    Storage.setItem(SKIP_CLIENT_CARD_VALIDATION, shouldSkipClientCardValidation);

                    checkOfferAndRewardOnCard({
                        rewardsEnabled,
                        rewardApplied: paymentResponse?.rewardApplied,
                        cardBinNumber,
                        selectedSplitPayMethods,
                        apiCallRef,
                        paymentPageOfferEnabled,
                        apiHost,
                        withSplitPay,
                        setCardOffer,
                        isPublicApiEnabled: isGetCardOfferPublicApiEnable,
                    });
                })
                .catch(() => {
                    setCardBrandDetails(defaultCardBrandDetails);
                    apiCallRef.current = false;
                });
        },
        [
            getCardDetailsFn,
            apiHost,
            bankCode,
            checkIfCardBankIsSameAsSelectedEmiBank,
            rewardsEnabled,
            paymentResponse?.rewardApplied,
            selectedSplitPayMethods,
            paymentPageOfferEnabled,
            withSplitPay,
            isGetCardOfferPublicApiEnable,
        ],
    );

    // Fetching this onBlur to reduce number of api calls/ fetch after user moves to different field
    const getCardFingerprint = useCallback(
        async (cardNumber: string) => {
            const enableCardRateLimit = isCardRateLimitEnabled(configContext);
            if (!enableCardRateLimit || cardNumber.length < MINIMUM_CARDNUMBER_LENGTH) {
                return;
            }

            // check if the cardNumber is same as of previous so to avoid fetching in case of same card Number
            if (!isEmpty(cardFingerPrint) && cardFingerPrint.cardNumber === cardNumber) {
                return;
            }
            const merchantId = paymentResponse?.juspayMeta.merchantId;
            const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);

            setCardFingerPrintCallInProgress(true);

            //check token validity and return updated token if expired or existing if not
            let validToken: string;
            try {
                validToken = await getValidClientAuthToken(
                    fetchJuspayClientAuthToken,
                    clientAuthTokenData,
                    paymentResponse ? resolveMerchantId(paymentResponse, VALID_PAYMENT_GROUP_TYPES.CARD) : "",
                    paymentResponse?.tenant,
                );
            } catch (err) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.ERROR_IN_JUSPAY_TOKEN,
                });
                captureError(err);
                return;
            }

            fetchCardFingerprint({ cardNumber, userId, merchantId, authToken: validToken })
                .then((data) => {
                    if (isEmpty(data)) {
                        setCardFingerPrint(null);
                    } else {
                        setCardFingerPrint({ ...data, cardNumber });
                    }
                })
                .catch(() => {
                    setCardFingerPrint(null);
                })
                .then(() => {
                    setCardFingerPrintCallInProgress(false);
                });
        },

        [configContext, cardFingerPrint, paymentResponse, fetchJuspayClientAuthToken, clientAuthTokenData],
    );

    const header = <CommonHeaderContent title={HEADER_TITLE.ADD_NEW_CARD} subtitle={subtitleText} grayscale />;

    const content = (
        <PaymentActionProvider>
            {({ onPay }): ReactElement => (
                <>
                    <AddNewCard
                        paymentInProgress={placeOrderInProgress || processingOrder}
                        apiInProgress={cardFingerPrintCallInProgress}
                        methodData={data.paymentMethod}
                        options={cardGroupOptions}
                        onPay={onPay}
                        juspayTokenizationMeta={paymentResponse?.juspayMeta?.tokenizationMeta}
                        onSecureCardInfoView={onSecureCardInfoView}
                        onSecureCardInfoClickAction={onSecureCardInfoClickAction}
                        onKnowMoreTokenizationClickAction={onKnowMoreTokenizationClickAction}
                        onCardNumberChange={getCardDetailsByBin}
                        cardBrandDetails={cardBrandDetails}
                        onProceedToPayClick={onProceedToPayClick}
                        onSaveCardClick={onSaveCardClick}
                        onProceedToPayClickAction={onProceedToPayClickAction}
                        cardOffer={cardOffer}
                        cardFingerPrintData={cardFingerPrint}
                        onCardNumberBlur={getCardFingerprint}
                        skipClientCardValidation={skipClientCardValidation}
                        isCardBankSameAsSelectedEmiBank={isCardBankSameAsSelectedEmiBank}
                        emiData={{ tenure, bankCode, emiBank, emiType, txnEmiType, bankName }}
                        showCrouton={setCroutonState}
                        isCardDiscountStripEnabled={isCardDiscountStripEnabled}
                        saveCardCtaHelperText={saveCardCtaHelperText}
                    />
                    <GenericCrouton {...croutonState} wrapperClassName={styles.croutonWrapper} />
                </>
            )}
        </PaymentActionProvider>
    );

    useEffectOnce(() => {
        getBridgeSdk()
            .setScreenCaptureState({ screenCapturingDisabled: true })
            .catch(() => void 0);

        return () => {
            getBridgeSdk()
                .setScreenCaptureState({ screenCapturingDisabled: false })
                .catch(() => void 0);
        };
    });

    return (
        <>
            <CommonUIWrapper header={header} content={content} containerCSSClass={styles.container} />
            {(placeOrderInProgress || processingOrder) && <PaymentProgressLoader />}
        </>
    );
});
